import { TFunction } from 'i18next';
import { formatDecimal } from '../../../shared/utils/formatters';
import {
  isBlockingS4DIssueKind,
  S4DIssueKind,
  S4DProductComparison,
  S4DProductStatus,
  S4DToppingRef,
} from './s4dProductComparison';

type Options = {
  comparison: S4DProductComparison;
  t: TFunction;
  getIngredientName: (ingredientId: number) => string | null | undefined;
};

export enum S4DPage {
  Product = 'product',
  Prices = 'prices',
}

export const isBlockingS4DIssue = (issue: S4DIssue): boolean => isBlockingS4DIssueKind(issue.kind);

export type S4DIngredientLabel = {
  ingredientId?: number;
  name: string;
  note?: string;
  topping?: S4DToppingRef;
};

export type S4DToppingAction = {
  label: string;
  toppings: S4DIngredientLabel[];
};

export type S4DPriceDetail = {
  label: string;
  value: string;
};

export const getS4DIngredientText = (label: S4DIngredientLabel): string =>
  `${label.ingredientId ? `${label.ingredientId} - ` : ''}${label.name}${label.note ? ` (${label.note})` : ''}`;

export type S4DIssue = {
  kind: S4DIssueKind;
  title: string;
  ingredient?: S4DIngredientLabel;
  detail?: string;
  prices?: S4DPriceDetail[];
  page?: S4DPage;
  s4dProductId?: number;
  toppingAction?: S4DToppingAction;
};

export const getS4DIssueText = (issue: S4DIssue): string => {
  const detail = [
    issue.ingredient ? getS4DIngredientText(issue.ingredient) : undefined,
    issue.detail,
    issue.prices?.map((price) => `${price.label} ${price.value}`).join(', '),
  ]
    .filter(Boolean)
    .join(': ');

  const text = detail ? `${issue.title}: ${detail}` : issue.title;
  if (!issue.toppingAction) return text;

  return `${text}. ${issue.toppingAction.label}: ${issue.toppingAction.toppings
    .map(getS4DIngredientText)
    .join(', ')}`;
};

export const getS4DIssues = (options: Options): S4DIssue[] => {
  const { comparison, t, getIngredientName } = options;

  if (comparison.status === S4DProductStatus.NotCompared) return [];

  const ingredientLabel = (
    ingredientId: number | null | undefined,
    toppings: S4DToppingRef[] = [],
    note?: string
  ): S4DIngredientLabel => {
    const topping = toppings.length === 1 ? toppings[0] : undefined;

    const notes = [
      topping?.isDuplicate ? t('duplicate') : undefined,
      ingredientId ? undefined : t('no web code in S4D'),
      note,
    ].filter(Boolean);

    return {
      ingredientId: ingredientId || undefined,
      name: (ingredientId ? getIngredientName(ingredientId) : undefined) || toppings[0]?.name || '',
      note: notes.join(', ') || undefined,
      topping,
    };
  };

  const toppingAction = (toppings: S4DToppingRef[], isRemoval: boolean): S4DToppingAction | undefined => {
    if (toppings.length === 0) return undefined;

    const label = isRemoval
      ? toppings.length === 1
        ? t('Remove topping')
        : t('Remove toppings')
      : toppings.length === 1
      ? t('Add topping')
      : t('Add toppings');

    return { label, toppings: toppings.map((topping) => ingredientLabel(topping.ingredientId, [topping])) };
  };

  const copiesToOpen = (toppings: S4DToppingRef[], isRemoval: boolean): S4DToppingAction | undefined =>
    toppings.length > 1 ? toppingAction(toppings, isRemoval) : undefined;

  const issues: S4DIssue[] = [];

  if (!comparison.hasMatchingWebCode) {
    issues.push({ kind: S4DIssueKind.ProductMissing, title: t('Product or WebCode missing from S4D') });
  }

  if (!comparison.hasPriceOption && comparison.hasMatchingWebCode) {
    issues.push({
      kind: S4DIssueKind.PriceMissing,
      title: `${t('global.price')} ${t('missing')}`,
      page: S4DPage.Prices,
    });
  }

  if (comparison.hasNoActiveS4DProduct) {
    issues.push({
      kind: S4DIssueKind.ProductInactive,
      title: t('S4D product is not active'),
      detail: comparison.inactiveS4DProducts.map((s4dProduct) => s4dProduct.name).join(', '),
      page: S4DPage.Product,
      s4dProductId:
        comparison.inactiveS4DProducts.length === 1 ? comparison.inactiveS4DProducts[0].s4dProductId : undefined,
    });
  } else {
    comparison.inactiveS4DProducts.forEach((s4dProduct) => {
      issues.push({
        kind: S4DIssueKind.ProductLeftover,
        title: t('S4D has an inactive product with the same web code'),
        detail: s4dProduct.name,
        page: S4DPage.Product,
        s4dProductId: s4dProduct.s4dProductId,
      });
    });
  }

  comparison.nameMismatches.forEach((mismatch) => {
    issues.push({
      kind: S4DIssueKind.NameDiffers,
      title: t('Name differs'),
      detail: t('S4D name: {{s4dName}}', { s4dName: mismatch.s4dName }),
      page: S4DPage.Product,
      s4dProductId: mismatch.s4dProductId,
    });
  });

  comparison.missingOptions.forEach((missing) => {
    issues.push({
      kind: S4DIssueKind.OptionMissing,
      title: t('Size or dough options missing from S4D'),
      detail: missing.webCode,
      page: S4DPage.Prices,
      s4dProductId: missing.s4dProductId,
    });
  });

  comparison.extraOptions.forEach((extra) => {
    issues.push({
      kind: S4DIssueKind.OptionOnlyInS4D,
      title: t('Size or dough options only in S4D'),
      detail: extra.webCode,
      page: S4DPage.Prices,
      s4dProductId: extra.s4dProductId,
    });
  });

  comparison.missingToppings.forEach((missing) => {
    issues.push({
      kind: S4DIssueKind.ToppingMissing,
      title: t('Toppings missing from the S4D recipe'),
      ingredient: ingredientLabel(
        missing.ingredientId,
        missing.s4dToppings,
        missing.s4dToppings.length === 0
          ? t('topping with web code {{webCode}} not found in S4D', { webCode: missing.ingredientId })
          : undefined
      ),
      detail: missing.recipeQuantity > 1 ? t('{{quantity}} pcs', { quantity: missing.recipeQuantity }) : undefined,
      page: S4DPage.Product,
      s4dProductId: missing.s4dProductId,
      toppingAction: copiesToOpen(missing.s4dToppings, false),
    });
  });

  comparison.toppingCountMismatches.forEach((mismatch) => {
    issues.push({
      kind: S4DIssueKind.ToppingCountDiffers,
      title: t('Topping counts differ from S4D'),
      ingredient: ingredientLabel(mismatch.ingredientId),
      detail: t('recipe {{recipeQuantity}}, S4D {{s4dCount}}', {
        recipeQuantity: mismatch.recipeQuantity,
        s4dCount: mismatch.s4dCount,
      }),
      page: S4DPage.Product,
      s4dProductId: mismatch.s4dProductId,
      toppingAction: toppingAction(mismatch.s4dToppings, mismatch.s4dCount > mismatch.recipeQuantity),
    });
  });

  comparison.extraS4DToppings.forEach((extra) => {
    issues.push({
      kind: S4DIssueKind.ToppingOnlyInS4D,
      title: t('Toppings only in the S4D recipe'),
      ingredient: ingredientLabel(extra.topping.ingredientId, extra.s4dToppings),
      detail: extra.s4dProductName,
      page: S4DPage.Product,
      s4dProductId: extra.s4dProductId,
      toppingAction: copiesToOpen(extra.s4dToppings, true),
    });
  });

  comparison.priceMismatches.forEach((mismatch) => {
    const prices = [
      mismatch.ourPrice !== undefined
        ? { label: t('webshop price'), value: formatDecimal(mismatch.ourPrice, ' €') }
        : undefined,
      mismatch.s4dDeliveryPrice !== undefined
        ? { label: t('S4D delivery price'), value: formatDecimal(mismatch.s4dDeliveryPrice, ' €') }
        : undefined,
      mismatch.s4dPickupPrice !== undefined
        ? { label: t('S4D pickup price'), value: formatDecimal(mismatch.s4dPickupPrice, ' €') }
        : undefined,
      mismatch.pickupPriceMissing ? { label: t('S4D pickup price'), value: t('missing') } : undefined,
    ].filter((price): price is S4DPriceDetail => price !== undefined);

    issues.push({
      kind: S4DIssueKind.PriceDiffers,
      title: t('Prices differ from S4D'),
      detail: t('S4D option {{webCode}}', { webCode: mismatch.optionWebCode || mismatch.productSizeId }),
      prices,
      page: S4DPage.Prices,
      s4dProductId: mismatch.s4dProductId,
    });
  });

  return issues;
};
